import React, { useEffect, useRef, useState } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { PageId } from "../types/page";
import { LogOut, HelpCircle, Menu } from "lucide-react";
import { apiGet } from "../utils/apiClient";

import DashboardPage from "./DashboardPage";
import TargetDashboardPage from "./TargetDashboardPage";
import MonthlyProfitPage from "./MonthlyProfitPage";
import DailyProfitPage from "./DailyProfitPage";
import PeriodSnapshotsPage from "./PeriodSnapshotsPage";
import PromotionPage from "./PromotionPage";
import AdsAnalysisPage from "./AdsAnalysisPage";
import SingleProfitPage from "./SingleProfitPage";
import ItemProfitDetailPage from "./ItemProfitDetailPage";
import ItemProfitSummaryPage from "./ItemProfitSummaryPage";
import ShippingCostSettingsPage from "./ShippingCostPage";
import ProductPLPage from "./ProductPLPage";
import LTVPage from "./LTVPage";
import AiAnalysisPage from "./AiAnalysisPage";
import SettingsPage from "./SettingsPage";
import PlanBillingPage from "./PlanBillingPage";
import UserManagementPage from "./UserManagementPage";
import RmsManualPage from "./RmsManualPage";
import MonthlyItemCostPage from "./MonthlyItemCostPage";
import MonthlyRakutenExpensePage from "./MonthlyRakutenExpensePage";
import FiscalRakutenExpensePage from "./FiscalRakutenExpensePage";
import VariableCostSettingsPage from "./VariableCostSettingsPage";
import AmazonFixedCostPage from "./AmazonFixedCostPage";
import YahooFixedCostPage from "./YahooFixedCostPage";
import YahooItemCostPage from "./YahooItemCostPage";
import YahooVariableCostPage from "./YahooVariableCostPage";
import AmazonItemCostPage from "./AmazonItemCostPage";
import AmazonVariableCostPage from "./AmazonVariableCostPage";
import AmazonSettingsPage from "./AmazonSettingsPage";
import YahooSettingsPage from "./YahooSettingsPage";
import CategoryAnalysisPage from "./productAnalysis/CategoryAnalysisPage";
import ProductNameAnalysisPage from "./productAnalysis/ProductNameAnalysisPage";
import ProductAnalysisPage from "./productAnalysis/ProductAnalysisPage";

import CompanySelector from "../components/CompanySelector";
import ContactPage from "./ContactPage";

import { useUserContext } from "../contexts/UserContext";
import { MallProvider } from "../contexts/MallContext";
import { useMallContext } from "../contexts/MallContext";
import MallInitializer from "../components/MallInitializer";
import YahooReauthBanner from "../components/YahooReauthBanner";
import RmsStopBanner from "../components/RmsStopBanner";
import { logout } from "../utils/firebaseAuth";
import { isFreePlanUser, isLightPlanUser, isStandardEquivalentPlan } from "../utils/plan";
import FreePlanUpgradeDialog, { UpgradeFeature } from "../components/FreePlanUpgradeDialog";

import LoadingInline from "../components/LoadingInline";

function lsGet(key: string) {
    if (typeof window === "undefined") return "";
    return String(localStorage.getItem(key) || "").trim();
}

function ssGet(key: string) {
    if (typeof window === "undefined") return "";
    return String(sessionStorage.getItem(key) || "").trim();
}

function normalizeCompanyRow(raw: any) {
    return {
        tenantId: String(raw?.tenantId || "").trim(),
        companyId: String(raw?.companyId || raw?.accountId || "").trim(),
        accountId: String(raw?.accountId || raw?.companyId || "").trim(),
        companyName: String(raw?.companyName || "").trim(),
        role: String(raw?.role || "").trim(),
        status: String(raw?.status || "").trim(),
        shopId: String(raw?.shopId || "").trim(),
        shopUrl: String(raw?.shopUrl || "").trim(),
        shopName: String(raw?.shopName || "").trim(),
        plan: String(raw?.plan || "").trim(),
        trialStatus: String(raw?.trialStatus || "").trim(),
    };
}

function sleep(ms: number) {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
}

// route→ページ名（ブラウザタブ表示用。MallAwarePage の pageLabel と同じ表記に揃える）
const PAGE_TITLES: Record<string, string> = {
    dashboard: "ダッシュボード",
    targetDashboard: "目標管理",
    monthly: "月次利益",
    daily: "日次利益",
    dailyOld: "日次利益（旧）",
    periodSnapshots: "期間利益",
    categoryAnalysis: "カテゴリー分析",
    productNameAnalysis: "商品名分析",
    productAnalysis: "商品分析",
    promotion: "販促分析",
    ads: "広告分析",
    single: "商品別利益（単体）",
    itemProfitDetail: "商品別利益（詳細）",
    itemProfitSummary: "商品別利益（集計）",
    shippingCostSettings: "配送コスト設定",
    product: "商品PL",
    ltv: "LTV分析",
    aiAnalysis: "AI分析",
    planBilling: "プラン・お支払い",
    itemCost: "商品原価",
    rakutenExpense: "楽天固定費設定",
    fiscalRakutenExpense: "固定費入力",
    variableCostSettings: "変動費入力",
    users: "ユーザー管理",
    settings: "初期設定",
    rmsManual: "過去月データ取得",
    contact: "お問い合わせ",
    amazonSettings: "Amazon連携設定",
    yahooSettings: "Yahoo連携設定",
};

function CurrentMallBadge() {
    const { mall } = useMallContext();
    const isAmazon = mall === "amazon";

    return (
        <span
            className={[
                "inline-flex items-center",
                "px-2 py-0.5 rounded-full text-[11px] font-semibold",
                "border",
                isAmazon
                    ? "bg-amber-50 text-amber-700 border-amber-200"
                    : "bg-rose-50 text-rose-700 border-rose-200",
            ].join(" ")}
            title={`表示モール: ${isAmazon ? "Amazon" : "楽天"}`}
        >
            {isAmazon ? "Amazon 表示" : "楽天 表示"}
        </span>
    );
}

function AmazonPrototypePanel({ pageLabel }: { pageLabel: string }) {
    return (
        <div className="mx-auto max-w-3xl rounded-xl border border-amber-200 bg-amber-50 p-6">
            <h2 className="text-lg font-bold text-amber-800">Amazon プロトタイプ表示</h2>
            <p className="mt-2 text-sm text-amber-700">
                現在の選択モールは Amazon です。{pageLabel} は本番実装前のため、プロトタイプ画面を表示しています。
            </p>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="rounded-lg border border-amber-200 bg-white p-3 text-sm text-slate-600">
                    1. サイドバーの「Amazon連携設定」で Seller ID を設定
                </div>
                <div className="rounded-lg border border-amber-200 bg-white p-3 text-sm text-slate-600">
                    2. 「ダッシュボード」「月次利益」「日次利益」で Amazon の実績を確認
                </div>
            </div>
        </div>
    );
}

function YahooComingSoonPanel({ pageLabel }: { pageLabel: string }) {
    return (
        <div className="mx-auto max-w-3xl rounded-xl border border-amber-200 bg-amber-50 p-6">
            <h2 className="text-lg font-bold text-amber-800">Yahoo!ショッピング 準備中</h2>
            <p className="mt-2 text-sm text-amber-700">
                現在の選択モールは Yahoo!ショッピングです。{pageLabel} は現在準備中です。
                サイドバーの「Yahoo連携設定」から店舗を登録すると、データ取得を開始します。
            </p>
        </div>
    );
}

function MallAwarePage({
    pageLabel,
    rakuten,
    amazon,
    yahoo,
}: {
    pageLabel: string;
    rakuten: React.ReactNode;
    amazon?: React.ReactNode;
    yahoo?: React.ReactNode;
}) {
    const { mall } = useMallContext();
    if (mall === "amazon") {
        return <>{amazon ?? <AmazonPrototypePanel pageLabel={pageLabel} />}</>;
    }
    if (mall === "yahoo") {
        return <>{yahoo ?? <YahooComingSoonPanel pageLabel={pageLabel} />}</>;
    }
    return <>{rakuten}</>;
}

/**
 * フリー／ライトプランのユーザーには、対象ページを開いたときに
 * 「スタンダードプラン以上でご利用いただけます」ポップアップを表示するゲート。
 * スタンダード以上のユーザーには通常どおり children を表示する。
 */
function FreePlanGate({
    featureName,
    features,
    description,
    children,
}: {
    featureName: string;
    features?: UpgradeFeature[];
    description?: React.ReactNode;
    children: React.ReactNode;
}) {
    const user = useUserContext();
    const restricted = React.useMemo(
        () => isFreePlanUser(user) || isLightPlanUser(user),
        [user]
    );
    const [dialogOpen, setDialogOpen] = useState(true);

    if (!restricted) return <>{children}</>;

    return (
        <div className="relative">
            <div className="pointer-events-none select-none opacity-50">
                <div className="mx-auto mt-8 max-w-3xl rounded-xl border border-slate-200 bg-white p-10 text-center text-slate-400">
                    {featureName}
                </div>
            </div>
            <FreePlanUpgradeDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                featureName={featureName}
                features={features}
                description={description}
            />
        </div>
    );
}

export default function AppShell() {
    const navigate = useNavigate();
    const location = useLocation();
    const user = useUserContext();

    const basePrefix = location.pathname.startsWith("/demo") ? "/demo" : "/app";
    const isDemo = basePrefix === "/demo";

    const storedShopUrl = lsGet("selectedShopUrl") || lsGet("shopUrl");
    const shopUrlForPages = isDemo
        ? "demo-shop"
        : (storedShopUrl || String((user as any)?.shopUrl || "").trim());

    const [planForHeader, setPlanForHeader] = useState<string>(() => {
        const userPlan = String((user as any)?.plan ?? "").trim();
        const initialPlan = userPlan || lsGet("plan");
        console.log("[AppShell] initial planForHeader", { userPlan, initialPlan });
        return initialPlan;
    });

    const [trialStatusForHeader, setTrialStatusForHeader] = useState<string>(() => {
        const userTrial = String((user as any)?.trialStatus ?? "").trim();
        return userTrial || lsGet("trialStatus");
    });

    // ✅ 削除：refreshPlanFromStorage() 関数は不要
    // useEffect で直接管理する

    const [companySwitching, setCompanySwitching] = useState(false);
    const [contentKey, setContentKey] = useState(0);
    const switchSeqRef = useRef(0);

    const waitForUserLoading = async () => {
        // UserContext の reading が終わるまで待つ（既に /api/me/company を呼んでいるので重複を避ける）
        const maxWaitMs = 10000;
        const started = Date.now();
        while (Date.now() - started < maxWaitMs) {
            if (!(user as any)?.loading) {
                console.log("[AppShell] UserContext ready");
                return true;
            }
            await sleep(100);
        }
        console.warn("[AppShell] timeout waiting for user loading");
        return false;
    };

    const beginCompanySwitch = async () => {
        const seq = ++switchSeqRef.current;
        setCompanySwitching(true);

        try {
            const ready = await waitForUserLoading();

            if (seq !== switchSeqRef.current) return;

            setContentKey((k) => k + 1);

            console.log("[AppShell] company switch completed", { ready });
        } finally {
            if (seq === switchSeqRef.current) {
                setCompanySwitching(false);
            }
        }
    };

    useEffect(() => {
        const userPlan = String((user as any)?.plan ?? "").trim();
        const userTrial = String((user as any)?.trialStatus ?? "").trim();

        if (userPlan) {
            localStorage.setItem("plan", userPlan);
        }
        if (userTrial) {
            localStorage.setItem("trialStatus", userTrial);
        }

        setPlanForHeader(userPlan || lsGet("plan"));
        setTrialStatusForHeader(userTrial || lsGet("trialStatus"));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [(user as any)?.plan, (user as any)?.trialStatus]);

    useEffect(() => {
        if (!String((user as any)?.shopUrl ?? "").trim()) return;
        localStorage.setItem("shopUrl", String((user as any)?.shopUrl ?? "").trim());
    }, [(user as any)?.shopUrl]);

    useEffect(() => {
        const sp = new URLSearchParams(window.location.search);

        const tenantId = sp.get("tenantId");
        const companyId = sp.get("companyId");
        const shopUrl = sp.get("shopUrl");

        const impTenantId = sp.get("impTenantId");
        const impCompanyId = sp.get("impCompanyId") || sp.get("superImpersonateCompanyId");
        const impShopUrl = sp.get("impShopUrl");

        if (tenantId) {
            localStorage.setItem("selectedTenant", tenantId);
            sessionStorage.setItem("login_tenant", tenantId);
        }
        if (companyId) {
            localStorage.setItem("companyId", companyId);
            localStorage.setItem("selectedCompanyId", companyId);
            localStorage.setItem("accountId", companyId);
        }
        if (shopUrl) {
            localStorage.setItem("shopUrl", shopUrl);
            localStorage.setItem("selectedShopUrl", shopUrl);
        }

        if (impTenantId) {
            localStorage.setItem("impTenantId", impTenantId);
        }
        if (impCompanyId) {
            localStorage.setItem("impCompanyId", impCompanyId);
            localStorage.setItem("superImpersonateCompanyId", impCompanyId);
        }
        if (impShopUrl) {
            localStorage.setItem("impShopUrl", impShopUrl);
        }
    }, []);

    useEffect(() => {
        if (typeof window === "undefined") return;

        const onChanged = async () => {
            await beginCompanySwitch();
        };

        window.addEventListener("company-selection-changed", onChanged);
        return () => window.removeEventListener("company-selection-changed", onChanged);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (typeof window === "undefined") return;

        const onMallChanged = () => {
            setContentKey((k) => k + 1);
        };

        window.addEventListener("mall-selection-changed", onMallChanged);
        return () => window.removeEventListener("mall-selection-changed", onMallChanged);
    }, []);

    let currentPath = location.pathname.replace(basePrefix, "").replace(/^\//, "");
    if (!currentPath) currentPath = "dashboard";

    const knownPages: PageId[] = [
        "dashboard",
        "targetDashboard",
        "monthly",
        "daily",
        "dailyOld",
        "periodSnapshots",
        "categoryAnalysis",
        "productNameAnalysis",
        "productAnalysis",
        "promotion",
        "ads",
        "single",
        "itemProfitDetail",
        "itemProfitSummary",
        "shippingCostSettings",
        "product",
        "ltv",
        "aiAnalysis",
        "planBilling",
        "itemCost",
        "rakutenExpense",
        "fiscalRakutenExpense",
        "variableCostSettings",
        "users",
        "settings",
        "rmsManual",
        "contact",
        "amazonSettings",
        "yahooSettings",
    ];

    const currentPage: PageId =
        (knownPages as unknown as string[]).includes(currentPath)
            ? (currentPath as PageId)
            : "dashboard";

    useEffect(() => {
        const label = PAGE_TITLES[currentPage];
        document.title = label ? `${label}｜利益Navi` : "利益Navi";
    }, [currentPage]);

    const setPage = (pageId: PageId) => navigate(`${basePrefix}/${pageId}`);

    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [isDesktop, setIsDesktop] = useState(false);

    useEffect(() => {
        const mq = window.matchMedia("(min-width: 768px)");
        const sync = () => setIsDesktop(mq.matches);
        sync();

        if ((mq as any).addEventListener) {
            (mq as any).addEventListener("change", sync);
            return () => (mq as any).removeEventListener("change", sync);
        } else {
            // @ts-ignore
            mq.addListener(sync);
            // @ts-ignore
            return () => mq.removeListener(sync);
        }
    }, []);

    const effectiveSidebarOpen = isDesktop ? true : sidebarOpen;

    const [helpOpen, setHelpOpen] = useState(false);
    const helpRef = useRef<HTMLDivElement | null>(null);
    const [companyOptions, setCompanyOptions] = useState<any[]>([]);

    const spNow = new URLSearchParams(location.search);

    const impersonateCompanyId =
        lsGet("superImpersonateCompanyId") ||
        lsGet("impCompanyId") ||
        ssGet("superImpersonateCompanyId") ||
        ssGet("impCompanyId") ||
        String(spNow.get("superImpersonateCompanyId") || "").trim() ||
        String(spNow.get("impCompanyId") || "").trim();

    const impersonateTenantId =
        lsGet("impTenantId") ||
        ssGet("impTenantId") ||
        String(spNow.get("impTenantId") || "").trim();

    const isImpersonating =
        !!impersonateCompanyId ||
        !!impersonateTenantId;

    useEffect(() => {
        let cancelled = false;

        const loadCompanies = async () => {
            try {
                if (!(user as any)?.uid) {
                    if (!cancelled) setCompanyOptions([]);
                    return;
                }

                const res = await apiGet("/api/join-company/list");
                const json = await res.json();

                if (!res.ok || !json.success) {
                    alert("取得に失敗しました：" + (json.error ?? "不明なエラー"));
                } else {
                    const list = Array.isArray(json.companies) ? json.companies : [];

                    console.log("[joinCompany/list parsed]", json);
                    console.log("[companyOptions.length]", list.length, list);

                    if (!cancelled) {
                        setCompanyOptions(list);
                        localStorage.setItem("companies", JSON.stringify(list));
                    }
                }
            } catch (e) {
                console.error("[joinCompany/list] load failed", e);
                if (!cancelled) {
                    setCompanyOptions([]);
                }
            }
        };

        loadCompanies();
        return () => {
            cancelled = true;
        };
    }, [(user as any)?.uid]);

    const contextCompaniesRaw = Array.isArray((user as any)?.adminCompanies)
        ? (user as any).adminCompanies
        : [];

    const mergedCompanyMap = new Map<string, any>();
    for (const row of companyOptions) {
        const normalized = normalizeCompanyRow(row);
        if (!normalized.companyId || !normalized.shopUrl) continue;
        mergedCompanyMap.set(`${normalized.companyId}__${normalized.shopUrl}`, normalized);
    }
    for (const row of contextCompaniesRaw) {
        const normalized = normalizeCompanyRow(row);
        if (!normalized.companyId || !normalized.shopUrl) continue;
        const key = `${normalized.companyId}__${normalized.shopUrl}`;
        if (!mergedCompanyMap.has(key)) {
            mergedCompanyMap.set(key, normalized);
        }
    }

    const mergedCompanyOptions = Array.from(mergedCompanyMap.values());
    const hasMultipleShops = mergedCompanyOptions.length > 1;

    const currentCompanyId =
        lsGet("selectedCompanyId") ||
        lsGet("companyId") ||
        ssGet("selectedCompanyId") ||
        ssGet("companyId");

    const selectedCompany = mergedCompanyOptions.find((c: any) => {
        const id = String(c?.companyId || c?.accountId || "").trim();
        return id === currentCompanyId;
    });

    const shopLabelForHeader = String(
        selectedCompany?.companyName ||
        selectedCompany?.shopName ||
        selectedCompany?.shopUrl ||
        (user as any)?.shopName ||
        (user as any)?.shopUrl ||
        storedShopUrl ||
        ""
    ).trim();

    const showCompanySelector = !!(
        (user as any)?.superAdmin ||
        isImpersonating ||
        hasMultipleShops
    );

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (!helpRef.current) return;
            if (!helpRef.current.contains(e.target as Node)) setHelpOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const planBadgeClass = (() => {
        const p = String(planForHeader || "").toLowerCase();
        if (!p) return "bg-slate-100 text-slate-700 border-slate-200";
        if (p.includes("free") || p.includes("フリー")) return "bg-emerald-50 text-emerald-700 border-emerald-200";
        if (p.includes("trial") || p.includes("トライアル")) return "bg-amber-50 text-amber-700 border-amber-200";
        return "bg-indigo-50 text-indigo-700 border-indigo-200";
    })();

    const isFreePlan = (() => {
        const p = String(planForHeader || "").toLowerCase();
        return p.includes("free") || p.includes("フリー");
    })();

    return (
        <div className="bg-slate-50 text-slate-800">
            <MallProvider>
                <MallInitializer />
            <header
                className="
                    fixed top-0 left-0 right-0 h-14
                    bg-white border-b shadow-sm
                    flex items-center justify-between px-6
                    z-40
                    "
            >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                    <button
                        type="button"
                        onClick={() => setSidebarOpen(true)}
                        className="md:hidden flex items-center justify-center h-10 w-10 rounded-full hover:bg-slate-100"
                        title="メニュー"
                        aria-label="メニューを開く"
                    >
                        <Menu size={22} className="text-slate-700" />
                    </button>

                    <div className="flex items-center gap-2 min-w-0">
                        <button
                            type="button"
                            onClick={() => setPage("dashboard")}
                            title="ダッシュボードへ"
                            aria-label="ダッシュボードへ"
                            className="shrink-0 cursor-pointer"
                        >
                            <img
                                src="/logo_navi.png"
                                alt="利益Navi"
                                className="md:hidden h-8 sm:h-9 w-auto max-w-[160px] object-contain"
                            />
                            <img
                                src="/logo_navi.png"
                                alt="利益Navi"
                                className="hidden md:block h-8 sm:h-9 w-auto max-w-[160px] object-contain"
                            />
                        </button>

                        {!!planForHeader && (
                            <button
                                type="button"
                                onClick={() => setPage("planBilling")}
                                className={[
                                    "hidden sm:inline-flex items-center",
                                    "px-2 py-0.5 rounded-full text-[11px] font-semibold",
                                    "border",
                                    "hover:opacity-85 cursor-pointer",
                                    planBadgeClass,
                                ].join(" ")}
                                title={`プラン: ${planForHeader}${(isStandardEquivalentPlan(planForHeader) || planForHeader === "プロ") &&
                                    trialStatusForHeader === "トライアル"
                                    ? `（${trialStatusForHeader}）`
                                    : ""
                                    }`}
                            >
                                {planForHeader}
                                {(isStandardEquivalentPlan(planForHeader) || planForHeader === "プロ") &&
                                    trialStatusForHeader === "トライアル"
                                    ? `（${trialStatusForHeader}）`
                                    : ""}
                            </button>
                        )}

                        <CurrentMallBadge />
                    </div>
                </div>

                <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                    {isFreePlan && (
                        <button
                            type="button"
                            onClick={() => {
                                window.open("https://docs.google.com/forms/d/e/1FAIpQLSdrzL9WveXl_qdF3CwmYk6rFgrLDBfjbItdyuvT3pwrdVRveQ/viewform?usp=header", "_blank", "noopener,noreferrer");
                            }}
                            className="
                                inline-flex items-center justify-center
                                rounded-md border border-blue-600 bg-blue-600 text-white
                                hover:bg-blue-700
                                px-3 py-2 text-xs sm:text-sm font-semibold
                                whitespace-nowrap shadow-sm
                            "
                            title="フリープランのお問い合せはこちらから"
                        >
                            <span className="hidden sm:inline">フリープランのお問い合せはこちら</span>
                            <span className="sm:hidden">お問い合せ</span>
                        </button>
                    )}

                    <div ref={helpRef} className="relative">
                        <button
                            type="button"
                            onClick={() => setHelpOpen((v) => !v)}
                            className="flex items-center justify-center h-10 w-10 rounded-full hover:bg-slate-100"
                            title="ヘルプ"
                            aria-label="ヘルプ"
                            aria-expanded={helpOpen}
                        >
                            <HelpCircle size={22} className="text-slate-700" />
                        </button>

                        {helpOpen && (
                            <div
                                className="
                                    absolute right-0 mt-2 w-40
                                    rounded-xl border border-slate-200
                                    bg-white shadow-lg
                                    overflow-hidden z-50
                                    "
                            >
                                <button
                                    className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50"
                                    onClick={() => {
                                        setHelpOpen(false);
                                        window.open("https://docs.google.com/forms/d/e/1FAIpQLSdrzL9WveXl_qdF3CwmYk6rFgrLDBfjbItdyuvT3pwrdVRveQ/viewform?usp=header", "_blank", "noopener,noreferrer");
                                    }}
                                >
                                    お問い合わせ
                                </button>
                                {/*
                                <button
                                    className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50"
                                    onClick={() => {
                                        setHelpOpen(false);
                                        window.open("https://ec-vision.co.jp/profit-guide/", "_blank", "noopener,noreferrer");
                                    }}
                                >
                                    ユーザーガイド
                                </button>
                                */}
                            </div>
                        )}
                    </div>

                    {showCompanySelector && (
                        <div className="w-52">
                            <CompanySelector companies={mergedCompanyOptions} />
                        </div>
                    )}

                    {!showCompanySelector && !!shopLabelForHeader && (
                        <span className="text-xs text-slate-500">店舗: {shopLabelForHeader}</span>
                    )}

                    <button
                        onClick={async () => {
                            if (isDemo) {
                                setHelpOpen(false);
                                navigate("/demo/dashboard");
                                return;
                            }
                            const sp = new URLSearchParams(window.location.search);
                            const logoutTenantId =
                                user.tenantId ||
                                sessionStorage.getItem("login_tenant") ||
                                localStorage.getItem("selectedTenant") ||
                                sp.get("tenantId") ||
                                "";

                            localStorage.removeItem("ecx_access_token");
                            localStorage.removeItem("ecx_token_expiry");
                            localStorage.removeItem("selectedTenant");

                            localStorage.removeItem("companyId");
                            localStorage.removeItem("selectedCompanyId");
                            localStorage.removeItem("shopUrl");
                            localStorage.removeItem("selectedShopUrl");

                            localStorage.removeItem("plan");
                            localStorage.removeItem("trialStatus");
                            localStorage.removeItem("companies");

                            sessionStorage.removeItem("login_tenant");
                            sessionStorage.removeItem("ecx_oauth_state");
                            sessionStorage.removeItem("ecx_code_verifier");

                            localStorage.removeItem("impTenantId");
                            localStorage.removeItem("impCompanyId");
                            localStorage.removeItem("impShopUrl");

                            localStorage.removeItem("superImpersonateCompanyId");

                            sessionStorage.removeItem("impTenantId");
                            sessionStorage.removeItem("impCompanyId");
                            sessionStorage.removeItem("impShopUrl");
                            sessionStorage.removeItem("superImpersonateCompanyId");
                            await logout(logoutTenantId);
                            if (logoutTenantId === "ecx") {
                                navigate("/ecx/login");
                                return;
                            }
                            navigate("/login");
                        }}
                        className="
                            flex items-center gap-2
                            rounded-md bg-slate-100 text-slate-700
                            hover:bg-slate-200
                            px-3 py-1 text-sm
                            border border-slate-300
                            "
                        title={isDemo ? "デモではログアウトしません" : "ログアウト"}
                    >
                        <LogOut size={18} />
                        <span className="hidden sm:inline">ログアウト</span>
                    </button>
                </div>
            </header>

            {sidebarOpen && (
                <div className="md:hidden fixed inset-0 bg-black/30 z-20" onClick={() => setSidebarOpen(false)} />
            )}

            <aside
                className={`
          fixed top-14 left-0 w-[210px] h-[calc(100vh-56px)] overflow-y-auto border-r bg-white
          z-30 transition-transform
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
          md:translate-x-0
        `}
            >
                <Sidebar
                    page={currentPage}
                    setPage={(pageId: PageId) => {
                        setSidebarOpen(false);
                        setPage(pageId);
                    }}
                    sidebarOpen={effectiveSidebarOpen}
                    setSidebarOpen={setSidebarOpen}
                />
            </aside>

            <main
                className="
    ml-0 md:ml-[210px] mt-14
    min-h-[calc(100vh-56px)]
    p-5 lg:p-6
  "
            >
                {companySwitching && (
                    <div className="fixed top-14 bottom-0 left-0 md:left-[210px] right-0 z-50 bg-white flex items-center justify-center">
                        <LoadingInline />
                    </div>
                )}

                {/* Yahoo API連携の再認可期限バナー（Yahoo表示中の全ページ共通） */}
                <YahooReauthBanner onNavigateToSettings={() => setPage("yahooSettings")} />

                {/* RN-283: 楽天RMSにログインできず取得が止まっている間の赤い警告（楽天表示中の全ページ共通・閉じられない） */}
                <RmsStopBanner onNavigateToSettings={() => setPage("settings")} />

                <Routes key={contentKey}>
                    <Route path="settings" element={<SettingsPage />} />
                    <Route path="planBilling" element={<PlanBillingPage />} />
                    <Route path="dashboard" element={<MallAwarePage pageLabel="ダッシュボード" rakuten={<DashboardPage />} amazon={<DashboardPage />} yahoo={<DashboardPage />} />} />
                    <Route path="targetDashboard" element={
                        <FreePlanGate
                            featureName="目標設定"
                            features={[
                                { title: "売上・利益の目標を設定", desc: "ショップの月次目標を登録して管理できます" },
                                { title: "目標と実績を自動で比較", desc: "達成率をダッシュボードで可視化します" },
                            ]}
                            description={<>売上・利益の目標を立てて、日々の進捗を数字で追いたいときに活躍します。</>}
                        >
                            <MallAwarePage pageLabel="目標管理" rakuten={<TargetDashboardPage />} />
                        </FreePlanGate>
                    } />
                    <Route path="monthly" element={<MallAwarePage pageLabel="月次利益" rakuten={<MonthlyProfitPage />} amazon={<MonthlyProfitPage />} yahoo={<MonthlyProfitPage />} />} />
                    <Route path="daily" element={<MallAwarePage pageLabel="日次利益" rakuten={<DailyProfitPage />} amazon={<DailyProfitPage />} yahoo={<DailyProfitPage />} />} />
                    <Route path="periodSnapshots" element={<MallAwarePage pageLabel="期間利益" rakuten={<PeriodSnapshotsPage />} amazon={<PeriodSnapshotsPage />} yahoo={<PeriodSnapshotsPage />} />} />
                    {/* 分析系は楽天専用データ（RMS由来）のため、Amazon 表示時は楽天の数値を
                        出さないよう MallAwarePage でゲートする（モール取り違えの防止） */}
                    <Route path="categoryAnalysis" element={
                        <MallAwarePage pageLabel="カテゴリー分析" rakuten={
                            <FreePlanGate
                                featureName="カテゴリー分析"
                                features={[
                                    { title: "カテゴリー別に利益を集計", desc: "商品カテゴリーごとの売上・利益を把握できます" },
                                    { title: "伸びているカテゴリーを発見", desc: "注力すべき商品群を数字で判断できます" },
                                ]}
                                description={<>どのカテゴリーが儲かっているのかを、売上ではなく利益で判断したいときに活躍します。</>}
                            >
                                <CategoryAnalysisPage />
                            </FreePlanGate>
                        } />
                    } />
                    <Route path="productNameAnalysis" element={
                        <MallAwarePage pageLabel="商品名分析" rakuten={
                            <FreePlanGate
                                featureName="商品名分析"
                                features={[
                                    { title: "商品名（キーワード）で横断集計", desc: "商品名の単位で売上・利益を比較できます" },
                                    { title: "売れ筋キーワードを把握", desc: "検索されやすい商品傾向を数字で確認できます" },
                                ]}
                                description={<>商品名やキーワード単位で、利益に貢献している商品を見極めたいときに活躍します。</>}
                            >
                                <ProductNameAnalysisPage />
                            </FreePlanGate>
                        } />
                    } />
                    <Route path="productAnalysis" element={
                        <MallAwarePage pageLabel="商品分析" rakuten={
                            <FreePlanGate
                                featureName="商品分析"
                                features={[
                                    { title: "商品ごとの利益を詳細分析", desc: "個別商品の売上・利益・転換率などを確認できます" },
                                    { title: "流入経路・ランク別でも比較", desc: "商品の集客構造まで踏み込んで分析できます" },
                                ]}
                                description={<>個別の商品単位で、手残り利益まで踏み込んで分析したいときに活躍します。</>}
                            >
                                <ProductAnalysisPage />
                            </FreePlanGate>
                        } />
                    } />
                    <Route path="promotion" element={<MallAwarePage pageLabel="販促分析" rakuten={<PromotionPage />} />} />
                    <Route path="ads" element={<MallAwarePage pageLabel="広告分析" rakuten={<AdsAnalysisPage />} />} />
                    <Route path="single" element={<MallAwarePage pageLabel="商品別利益（単体）" rakuten={<SingleProfitPage />} />} />
                    <Route path="itemProfitDetail" element={<MallAwarePage pageLabel="商品別利益（詳細）" rakuten={<ItemProfitDetailPage />} amazon={<ItemProfitDetailPage />} yahoo={<ItemProfitDetailPage />} />} />
                    <Route path="itemProfitSummary" element={<MallAwarePage pageLabel="商品別利益（集計）" rakuten={<ItemProfitSummaryPage />} amazon={<ItemProfitSummaryPage />} yahoo={<ItemProfitSummaryPage />} />} />
                    <Route path="shippingCostSettings" element={<MallAwarePage pageLabel="配送コスト設定" rakuten={<ShippingCostSettingsPage />} />} />
                    <Route path="product" element={<MallAwarePage pageLabel="商品PL" rakuten={<ProductPLPage />} />} />
                    <Route path="ltv" element={<MallAwarePage pageLabel="LTV分析" rakuten={<LTVPage />} />} />
                    <Route path="aiAnalysis" element={<MallAwarePage pageLabel="AI分析" rakuten={<AiAnalysisPage />} />} />
                    <Route path="itemCost" element={<MallAwarePage pageLabel="商品原価" rakuten={<MonthlyItemCostPage shopUrl={shopUrlForPages} />} amazon={<AmazonItemCostPage />} yahoo={<YahooItemCostPage />} />} />
                    <Route path="rakutenExpense" element={<MallAwarePage pageLabel="楽天固定費設定" rakuten={<MonthlyRakutenExpensePage shopUrl={shopUrlForPages} />} />} />
                    <Route path="fiscalRakutenExpense" element={<MallAwarePage pageLabel="固定費入力" rakuten={<FiscalRakutenExpensePage shopUrl={shopUrlForPages} />} amazon={<AmazonFixedCostPage />} yahoo={<YahooFixedCostPage />} />} />
                    <Route path="variableCostSettings" element={<MallAwarePage pageLabel="変動費入力" rakuten={<VariableCostSettingsPage shopUrl={shopUrlForPages} />} amazon={<AmazonVariableCostPage />} yahoo={<YahooVariableCostPage />} />} />
                    <Route path="amazonSettings" element={<AmazonSettingsPage />} />
                    <Route path="yahooSettings" element={<YahooSettingsPage />} />
                    <Route path="users" element={<UserManagementPage />} />
                    <Route path="rmsManual" element={<MallAwarePage pageLabel="過去月データ取得" rakuten={<RmsManualPage />} />} />
                    <Route path="contact" element={<ContactPage />} />
                    <Route path="*" element={<DashboardPage />} />
                </Routes>
            </main>
            </MallProvider>
        </div>
    );
}